import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Col, Container, Row } from 'react-bootstrap';

import styles from './User.module.scss';
import { useTranslate } from 'react-polyglot';
import { useAuth } from '../../../Auth';
import { useAxios } from '../../../AxiosHandler';
import UserCardInfo from './UserCards/UserCardInfo';
import Card from '../../Shared/Card/Card';

function User(props: UserProps) {
    const t = useTranslate();
    const navigate = useNavigate();
    const auth = useAuth();
    const axiosHandler = useAxios();
    const axios = axiosHandler.axios;
    const params = useParams();
    const location = useLocation();

    // Listesidas query-streng (fanen, eller åpen seksjon på /admin) følger med hit
    // i history state, slik at knappene under leverer brukeren tilbake til det de
    // kom fra og ikke til standardvisningen. Åpnet man detaljsiden via en direkte
    // lenke finnes ingen state, og vi lander på listesida uendret.
    const listUrl = "/admin" + ((location.state as any)?.listSearch ?? "");


    const [user, setUser] = useState<any | undefined>();
    const [userBreeds, setUserBreeds] = useState<Array<any>>([]);
    const [emailPrefs, setEmailPrefs] = useState<Array<any>>([]);
    const [councils, setCouncils] = useState<Array<any>>([]);
    const [councilConnections, setCouncilConnections] = useState<Array<any>>([]);

    function getUser() {
        axios.single({
            method: 'get',
            url: "/api/users/byid/" + params.userid + "/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
           setUser(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function getCouncils() {
        axios.single({
            method: 'get',
            url: "/api/users/getCouncils",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setCouncils(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function getCouncilConnections() {
        axios.single({
            method: 'get',
            url: "/api/users/getCouncilConnections/" + params.userid,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setCouncilConnections(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function getUserBreeds() {
        axios.single({
            method: 'get',
            url: "/api/users/breedsForUser/" + params.userid,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setUserBreeds(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function getUserEmailPrefs() {
        axios.single({
            method: 'get',
            url: "/api/users/emailPrefs/" + params.userid,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setEmailPrefs(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    async function deleteUser() {
        return new Promise(async (resolve, reject) => {
            axios.single({
                method: 'delete',
                url: "/api/users/deleteUser/",
                data: { userid: params.userid },
                responseType: 'json',
                headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
            }).then((res: any) => {
                resolve(res.data);
            }).catch((err: any) => {
                console.log(err);
            });
        });
    }

    function resetPassword(password: string) {
        axios.single({
            method: 'post',
            url: "/api/users/resetPassword/",
            data: { userid: params.userid, password: password },
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
           setUser(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function getEmailPref(breedId: number, field: string): boolean {
        const pref = emailPrefs.find(p => Number(p.BREED_ID) === breedId);
        if (!pref) return false;
        return pref[field] === 1 || pref[field] === true;
    }

    const accessLabel = (level: string) => {
        if (level === 'admin') return t("user.edituser.councillist.admin");
        if (level === 'dogowner') return t("user.edituser.councillist.dogowner");
        return t("user.edituser.councillist.user");
    };

    useEffect(() => {
        if (params.userid != undefined && auth.selectedBreed != undefined) {
            getUser();
        }
    }, [params.userid, auth.selectedBreed]);

    useEffect(() => {
        if (params.userid != undefined) {
            getCouncils();
            getCouncilConnections();
            getUserBreeds();
            getUserEmailPrefs();
        }
    }, [params.userid]);

    const title = user
        ? `${user.FIRSTNAME ?? ''} ${user.LASTNAME ?? ''}`.trim() || user.EMAIL
        : t("user.title");

    return (
        <div className="maincontent">
            <div className={styles.user}>
                <h1>{title}</h1>
                <hr/>
                <div className={styles.topbuttons}>
                    <button className="btn btn-primary" onClick={() => navigate(listUrl)}>
                        {t("user.buttons.back")}
                    </button>
                    <button className="btn btn-primary" onClick={() => {
                        let answer = window.prompt(t("user.buttons.resetpasswordprompt"), "ValpeSommer123");
                        if (answer != null) resetPassword(answer);
                    }}>
                        {t("user.buttons.resetpassword")}
                    </button>
                    <button className="btn btn-primary" onClick={() => navigate("/edituser/" + params.userid, { state: location.state })}>
                        {t("user.buttons.edit")}
                    </button>
                    <button className="btn btn-primary" onClick={() => {
                        if (window.confirm(t("user.buttons.deleteconfirm"))) {
                            deleteUser().then(() => navigate(listUrl));
                        }
                    }}>
                        {t("user.buttons.delete")}
                    </button>
                </div>
                <hr/>
                {user != undefined &&
                    <Container className={styles.container}>
                        <Row className={styles.row}>
                            <Col sm="12" md="12" lg="6" xl="4" className={styles.col}>
                                <UserCardInfo
                                    title={t("user.cards.generaloverview.title")}
                                    user={user}
                                    userBreeds={userBreeds}
                                />
                            </Col>
                            {councilConnections.filter(cc => councils.find(c => c.ID == cc.COUNCIL_ID)).length > 0 &&
                                <Col sm="12" md="12" lg="6" xl="4" className={styles.col}>
                                    <Card title={t("user.edituser.councillist.title")}>
                                        <table className={styles.infoTable}>
                                            <tbody>
                                                {councilConnections
                                                    .filter((cc: any) => councils.find((c: any) => c.ID == cc.COUNCIL_ID))
                                                    .map((cc: any) => {
                                                        const council = councils.find((c: any) => c.ID == cc.COUNCIL_ID);
                                                        return (
                                                            <tr key={cc.COUNCIL_ID}>
                                                                <td className={styles.label}>{council?.NAME}</td>
                                                                <td className={styles.value}>{accessLabel(cc.ACCESSLEVEL)}</td>
                                                            </tr>
                                                        );
                                                    })
                                                }
                                            </tbody>
                                        </table>
                                    </Card>
                                </Col>
                            }
                            {userBreeds.length > 0 &&
                                <Col sm="12" md="12" lg="12" xl="8" className={styles.col}>
                                    <Card title={t("user.edituser.email_preferences.title")}>
                                        <table className={styles.emailPrefsGrid}>
                                            <thead>
                                                <tr>
                                                    <th></th>
                                                    <th>{t("user.edituser.email_preferences.breedingdog")}</th>
                                                    <th>{t("user.edituser.email_preferences.breedingrequest")}</th>
                                                    <th>{t("user.edituser.email_preferences.sickness")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {userBreeds.map((breed: any) => (
                                                    <tr key={breed.BREED_ID}>
                                                        <td>{breed.BREED_NAME}</td>
                                                        <td className={getEmailPref(breed.BREED_ID, 'EMAIL_BREEDINGDOG') ? styles.checkYes : styles.checkNo}>
                                                            {getEmailPref(breed.BREED_ID, 'EMAIL_BREEDINGDOG') ? '✓' : '—'}
                                                        </td>
                                                        <td className={getEmailPref(breed.BREED_ID, 'EMAIL_BREEDINGREQUEST') ? styles.checkYes : styles.checkNo}>
                                                            {getEmailPref(breed.BREED_ID, 'EMAIL_BREEDINGREQUEST') ? '✓' : '—'}
                                                        </td>
                                                        <td className={getEmailPref(breed.BREED_ID, 'EMAIL_SICKNESS') ? styles.checkYes : styles.checkNo}>
                                                            {getEmailPref(breed.BREED_ID, 'EMAIL_SICKNESS') ? '✓' : '—'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </Card>
                                </Col>
                            }
                        </Row>
                    </Container>
                }
            </div>
        </div>
    );
};

class UserProps {}

export default User;
